import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Settings = () => {
  const navigate = useNavigate();
  const { user, loading, getCurrentUser, isAuthenticated, getLocalUser } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Settings sections
  const settingsSections = [
    {
      id: 'profile',
      title: 'Profile Settings',
      description: 'Manage your personal information and account preferences',
      icon: 'User',
      path: '/profile',
      color: 'text-blue-600 bg-blue-50'
    },
    {
      id: 'appearance',
      title: 'Appearance & Customization',
      description: 'Customize the look and feel of your AI agents',
      icon: 'Palette',
      path: '/appearance-customization',
      color: 'text-purple-600 bg-purple-50'
    },
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Manage user accounts, permissions, and access controls',
      icon: 'Users',
      path: '/user-management-settings',
      color: 'text-green-600 bg-green-50'
    },
    {
      id: 'ai-configuration',
      title: 'AI Agent Configuration',
      description: 'Configure AI models, behavior, and conversation settings',
      icon: 'Brain',
      path: '/agent-configuration',
      color: 'text-orange-600 bg-orange-50'
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      description: 'Manage security settings, authentication, and data privacy',
      icon: 'Shield',
      path: '#security',
      color: 'text-red-600 bg-red-50'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Configure email and push notification preferences',
      icon: 'Bell',
      path: '#notifications',
      color: 'text-yellow-600 bg-yellow-50'
    },
    {
      id: 'integrations',
      title: 'Integrations & API',
      description: 'Manage third-party integrations and API access',
      icon: 'Plug',
      path: '#integrations',
      color: 'text-indigo-600 bg-indigo-50'
    },
    {
      id: 'backup',
      title: 'Backup & Data Export',
      description: 'Export your data and manage backup preferences',
      icon: 'Download',
      path: '#backup',
      color: 'text-gray-600 bg-gray-50'
    }
  ];

  useEffect(() => {
    // Only try to get current user if not already available and not loading
    if (!user && !loading && isAuthenticated && isAuthenticated()) {
      refreshUser();
    }
  }, [user, loading]);

  const refreshUser = async () => {
    if (!getCurrentUser) return;
    
    setUserLoading(true);
    setAuthError(null);
    
    try {
      const currentUser = await getCurrentUser();
      // User state will be updated automatically through AuthContext
      console.log('Current user:', currentUser ? 'Found' : 'Not found');
      
      if (!currentUser) {
        setAuthError('No active session found. Please log in.');
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      setAuthError('Failed to verify authentication status.');
    } finally {
      setUserLoading(false);
    }
  };

  // Enhanced user display logic
  const getUserDisplayInfo = () => {
    const localUser = getLocalUser?.();
    if (localUser?.email) {
      return {
        isSignedIn: true,
        email: localUser?.email,
        displayText: `Signed in as ${localUser?.email}`
      };
    }
    
    if (authError) {
      return {
        isSignedIn: false,
        error: authError,
        displayText: authError
      };
    }
    
    return {
      isSignedIn: false,
      displayText: 'Not signed in'
    };
  };

  const handleSectionClick = (section) => {
    if (section?.path?.startsWith('#')) {
      // Handle in-app navigation for sections not yet implemented
      console.log(`Navigate to ${section?.id} section`);
      // You can add specific handling here for each section
      return;
    }
    // Navigate to existing pages
    navigate(section?.path);
  };

  const SettingCard = ({ section }) => (
    <div 
      onClick={() => handleSectionClick(section)}
      className="group p-6 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-lg transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-lg ${section?.color} group-hover:scale-110 transition-transform duration-200`}>
          <Icon name={section?.icon} size={24} className={section?.color?.split(' ')?.[0]} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
            {section?.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {section?.description}
          </p>
        </div>
        <Icon 
          name="ChevronRight" 
          size={20} 
          className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-200" 
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const userInfo = getUserDisplayInfo();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <Header />
        
        <main className="p-6 pb-20 lg:pb-6">
          <div className="max-w-6xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Settings" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Settings</h1>
                  <p className="text-muted-foreground">
                    Manage your account, preferences, and application settings
                  </p>
                </div>
              </div>
              
              {/* Enhanced User Info Section */}
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="User" size={16} />
                {userLoading ? (
                  <span className="text-muted-foreground">Loading user info...</span>
                ) : userInfo?.isSignedIn ? (
                  <div className="flex items-center space-x-2">
                    <span className="text-muted-foreground">{userInfo?.displayText}</span>
                    <div className="w-2 h-2 bg-green-500 rounded-full" title="Authenticated"></div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className={`${userInfo?.error ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {userInfo?.displayText}
                    </span>
                    {userInfo?.error && (
                      <div className="w-2 h-2 bg-red-500 rounded-full" title="Authentication Error"></div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/login')}
                      className="ml-2"
                    >
                      Sign In
                    </Button>
                  </div>
                )}
              </div>

              {/* Error Message Display */}
              {authError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-red-600" />
                    <span className="text-sm text-red-800">{authError}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAuthError(null);
                        navigate('/login');
                      }}
                      className="ml-auto text-red-600 hover:text-red-800"
                    >
                      Sign In
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="User"
                  iconPosition="left"
                  onClick={() => navigate('/profile')}
                  className="hover:bg-primary/5"
                >
                  My Profile
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Palette"
                  iconPosition="left"
                  onClick={() => navigate('/appearance-customization')}
                  className="hover:bg-primary/5"
                >
                  Customize Appearance
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Users"
                  iconPosition="left"
                  onClick={() => navigate('/user-management-settings')}
                  className="hover:bg-primary/5"
                >
                  Manage Users
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Settings"
                  iconPosition="left"
                  onClick={() => navigate('/agent-configuration')}
                  className="hover:bg-primary/5"
                >
                  AI Configuration
                </Button>
              </div>
            </div>

            {/* Settings Grid */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-foreground">All Settings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {settingsSections?.map((section) => (
                  <SettingCard key={section?.id} section={section} />
                ))}
              </div>
            </div>

            {/* Footer Info */}
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                <div className="text-sm text-muted-foreground">
                  <p>Application Version: 1.0.0</p>
                  <p>Last Updated: September 2025</p>
                </div>
                <div className="flex items-center space-x-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="HelpCircle"
                    iconPosition="left"
                  >
                    Help & Support
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="left"
                  >
                    Documentation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Settings;